import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ROUTER_CART } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { ExportFileIcon } from 'assets/icons';
import { useModalActions } from 'domains/basket/Basket.hooks';
import {
  computeTotalPrice,
  getActionStatus,
  getBasketExternalSection,
  getBasketNumberOfItems,
  getBasketOtherSection,
  getBasketVehicles,
} from 'domains/basket/Basket.store';
import { VehicleLocal } from 'domains/basket/Basket.types';
import { getCurrency } from 'domains/user';
import { ScrollDiv, SDiv, SDivider } from 'pages/CartPage/CartPopover/CartPopover.styled';
import SectionCard from 'pages/CartPage/CartPopover/SectionCard';
import CartAddReference, { CartAddReferenceResult } from 'pages/CartPage/CartStep/CartAddReference';
import { theme } from 'styles';
import { BlackButton, Flex, Icon, PaddingBox, SPACE_BETWEEN, Text } from 'UI';
import { textFormatter } from 'utils';

const CartPopover = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { openModal } = useModalActions();

  const [addedOtherReference, setAddedOtherReference] = useState<string | undefined>(undefined);
  const setAddedByReferenceBox = useCallback((referenceNumber?: string) => {
    setAddedOtherReference(referenceNumber);
  }, []);

  const currency = useSelector(getCurrency);
  const otherSection = useSelector(getBasketOtherSection);
  const otherRefsActions = useSelector((state: RootState) => getActionStatus(state, addedOtherReference));

  const allVehicles = useSelector(getBasketVehicles);
  const allExternals = useSelector(getBasketExternalSection);
  const itemsCount = useSelector(getBasketNumberOfItems);

  const basketTotalPriceVatIncluded = computeTotalPrice(allVehicles, allExternals, otherSection?.references, true);

  const handleOnSubmit = useCallback(() => {
    history.push(ROUTER_CART);
  }, [history]);

  return (
    <SDiv data-cy={'cart-preview'}>
      <Flex direction={'column'} gap={8}>
        <Flex direction={'row'}>
          <Flex />
          <Flex alignSelf={'center'} minHeight={20}>
            <Text type={'h4_paragraph'} data-cy={'cart-preview-text'}>
              {t('cart.preview.title', 'My Cart')}
            </Text>
          </Flex>
          <Icon
            IconComponent={ExportFileIcon}
            round
            size={20}
            color={theme.color.brand_black}
            onClick={openModal}
            data-cy={'cart-preview-upload-button'}
          />
        </Flex>
        <SDivider />
        <ScrollDiv>
          <Flex direction={'column'} gap={8}>
            <PaddingBox pb={8}>
              <CartAddReference setAddedByReferenceBox={setAddedByReferenceBox} expandable />
              {otherRefsActions && (
                <CartAddReferenceResult
                  otherSection={otherSection}
                  addedOtherReference={addedOtherReference}
                  otherReferencesAction={otherRefsActions}
                />
              )}
            </PaddingBox>
            {allVehicles?.map((vehicle: VehicleLocal) => (
              <SectionCard
                key={vehicle.vehicleDetail.vehicleKey}
                vehicle={vehicle}
                data-cy={`cart-preview-vehicle-section-${vehicle.vehicleDetail.vin}`}
              />
            ))}
            {allExternals?.map((ext) => (
              <SectionCard
                key={ext.externalBasketId}
                externalBasket={ext}
                references={ext.references}
                data-cy={'cart-preview-external-references-section'}
              />
            ))}
            {otherSection && otherSection.references.length > 0 && (
              <SectionCard references={otherSection.references} data-cy={'cart-preview-other-articles-section'} />
            )}
          </Flex>
        </ScrollDiv>
        <SDivider />
        <Flex direction={'row'} justify={SPACE_BETWEEN} align={'center'} padding={'10px 0px 0px 0px'}>
          <Text type="lg" disableGutter data-cy={'cart-preview-free-delivery-text'}>
            {t('cart.preview.free_delivery', 'Free delivery')}
          </Text>
          <Text type="lg" data-cy={'cart-preview-references-count'}>
            {t('catalog.checkout_card.total.item2', '{{count}} items', { count: itemsCount })}
          </Text>
        </Flex>
        <Flex direction={'row'} justify={SPACE_BETWEEN} data-cy={'cart-preview-total'} padding={'10px 0px 10px 0px'}>
          <Text type="heading3" disableGutter displayStyle={'default'} data-cy={'cart-preview-total-vat-incl'}>{`${t(
            'common.price.total_vat_incl',
            'Total VAT. Incl',
          )}:`}</Text>
          <Text type="heading3" disableGutter displayStyle={'default'} data-cy={'cart-preview-total-price'}>
            {textFormatter.formatCurrency(basketTotalPriceVatIncluded ?? 0, currency)}
          </Text>
        </Flex>
        <BlackButton
          onClick={handleOnSubmit}
          shape="semiRound"
          data-cy={'cart-preview-go-to-cart-button'}
          size={'xlarge'}
        >
          <Trans i18nKey={'cart.preview.action.go_to_cart'}>{'Go to cart'}</Trans>
        </BlackButton>
      </Flex>
    </SDiv>
  );
};

export default CartPopover;
